import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Trophy, Heart, ChevronRight } from 'lucide-react';
import { useExperienceTheme, themeConfigs } from '../context/ExperienceThemeContext';
import { useAuth } from '../context/AuthContext';

const ThemeSelection = () => {
  const navigate = useNavigate();
  const { selectTheme } = useExperienceTheme();
  const { user } = useAuth();

  const handleSelectTheme = async (themeId) => {
    await selectTheme(themeId);
    navigate('/dashboard');
  };

  const themes = [
    {
      ...themeConfigs.space,
      icon: Rocket,
      preview: 'Embark on a space mission where you\'re the pilot of your own journey.',
      features: ['Mission Control Dashboard', 'Pilot Status Updates', 'Dark Space Theme'],
      bgClass: 'bg-gradient-to-br from-cyan-900/50 to-purple-900/50',
      borderClass: 'border-cyan-500/30 hover:border-cyan-400',
      iconBg: 'bg-cyan-500/20',
      iconColor: 'text-cyan-400',
      textColor: 'text-white',
      textMuted: 'text-white/50',
    },
    {
      ...themeConfigs.sports,
      icon: Trophy,
      preview: 'Step into the arena where every day is game day and you\'re the star athlete.',
      features: ['Game Day Headquarters', 'Performance Tracking', 'Clean Light Theme'],
      bgClass: 'bg-white',
      borderClass: 'border-orange-500/50 hover:border-orange-500',
      iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600',
      iconColor: 'text-white',
      textColor: 'text-gray-900',
      textMuted: 'text-gray-600',
    },
    {
      ...themeConfigs.pet,
      icon: Heart,
      preview: 'Nurture your journey like caring for a beloved pet - with warmth and patience.',
      features: ['Pet Parent Dashboard', 'Cozy Check-ins', 'Warm Light Theme'],
      bgClass: 'bg-gradient-to-br from-pink-50 to-purple-50',
      borderClass: 'border-pink-400/50 hover:border-pink-500',
      iconBg: 'bg-gradient-to-br from-pink-400 to-pink-500',
      iconColor: 'text-white',
      textColor: 'text-gray-900',
      textMuted: 'text-gray-600',
    },
  ];

  return (
    <div className="min-h-screen bg-[#050508] flex flex-col">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0a0a15] via-[#050508] to-[#050508] opacity-50 pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 px-4 py-8 text-center">
        <h1 className="text-white text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Welcome, {user?.name?.split(' ')[0] || 'Friend'}!
        </h1>
        <p className="text-white/60 text-lg">
          Choose your experience
        </p>
      </header>

      {/* Theme Cards */}
      <main className="relative z-10 flex-1 px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-white/40 text-center text-sm mb-8">
            Select a theme that resonates with you. You can change this anytime in settings.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {themes.map((theme) => (
              <button
                key={theme.id}
                onClick={() => handleSelectTheme(theme.id)}
                className={`relative group p-6 rounded-2xl border-2 ${theme.borderClass} ${theme.bgClass} backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] text-left shadow-lg`}
                data-testid={`theme-${theme.id}`}
              >
                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl ${theme.iconBg} flex items-center justify-center mb-4 shadow-md`}>
                  <theme.icon className={`w-8 h-8 ${theme.iconColor}`} />
                </div>

                {/* Title */}
                <h2 className={`${theme.textColor} text-xl font-bold mb-1`}>{theme.name}</h2>
                <p className={`${theme.textMuted} text-sm mb-4`}>{theme.tagline}</p>

                {/* Preview */}
                <p className={`${theme.textMuted} text-sm mb-4`}>{theme.preview}</p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {theme.features.map((feature, idx) => (
                    <li key={idx} className={`${theme.textMuted} text-xs flex items-center gap-2`}>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.id === 'space' ? '#22d3ee' : theme.id === 'sports' ? '#ea580c' : '#ec4899' }} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className={`flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all`} style={{ color: theme.id === 'space' ? '#22d3ee' : theme.id === 'sports' ? '#ea580c' : '#ec4899' }}>
                  Choose {theme.name}
                  <ChevronRight size={16} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ThemeSelection;
